import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { resolveUserStatusChipColors } from '@/presentation/pages/users/utils/resolve-user-status-chip-colors.js';
import type { UserStatus } from '@/shared/utils/format-status.js';

interface Props {
    name: string;
    email: string;
    phone: string;
    status: UserStatus;
    isEdit: boolean;
    errors: Record<string, string>;
    labels: {
        personalData: string;
        nameLabel: string;
        namePlaceholder: string;
        emailLabel: string;
        emailPlaceholder: string;
        emailReadOnly: string;
        phoneLabel: string;
        phonePlaceholder: string;
        status: string;
        active: string;
        blocked: string;
    };
    onNameChange: (value: string) => void;
    onEmailChange: (value: string) => void;
    onPhoneChange: (value: string) => void;
}

export function UserDadosTab({ name, email, phone, status, isEdit, errors, labels, onNameChange, onEmailChange, onPhoneChange }: Props) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
            <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                    {labels.personalData}
                </Typography>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                            {labels.nameLabel}{' '}
                            <Typography component="span" color="error.main">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            value={name}
                            onChange={(e) => onNameChange(e.target.value)}
                            size="small"
                            fullWidth
                            error={!!errors.name}
                            helperText={errors.name}
                            placeholder={labels.namePlaceholder}
                            inputProps={{ maxLength: 200 }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                            {labels.emailLabel}{' '}
                            {!isEdit && (
                                <Typography component="span" color="error.main">
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            type="email"
                            value={email}
                            onChange={(e) => onEmailChange(e.target.value)}
                            size="small"
                            fullWidth
                            error={!!errors.email}
                            helperText={errors.email}
                            placeholder={labels.emailPlaceholder}
                            inputProps={{ maxLength: 300 }}
                            disabled={isEdit}
                            sx={isEdit ? { '& .MuiInputBase-input': { bgcolor: 'action.hover', color: 'text.disabled' } } : undefined}
                        />
                        {isEdit && (
                            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                                {labels.emailReadOnly}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                            {labels.phoneLabel}
                        </Typography>
                        <TextField value={phone} onChange={(e) => onPhoneChange(e.target.value)} size="small" fullWidth placeholder={labels.phonePlaceholder} inputProps={{ maxLength: 50 }} />
                    </Box>
                    {isEdit && (
                        <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                {labels.status}
                            </Typography>
                            <Chip label={status === 'ATIVO' ? labels.active : labels.blocked} size="small" sx={{ ...resolveUserStatusChipColors(status), fontWeight: 500 }} />
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
